import type { InfiniteData, QueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import type { CasinoGame, CasinoGamesResponse } from "../../types/gamePass";

import {
  getCasinoGameMatchIds,
  humanizeCasinoGameSlug,
  normalizeCasinoGameId,
  resolveCasinoGameLabel,
} from "./casinoGameNormalizers";
import {
  fetchCasinoGames,
  getNextCasinoGamesOffset,
  GAME_PASS_CASINO_GAMES_QUERY_KEY,
} from "./useGetCasinoGames";
import { fetchCasinoProviders } from "./useGetCasinoProviders";

export { normalizeCasinoGameId } from "./casinoGameNormalizers";

const stampLabelForMatchingIds = (
  game: CasinoGame,
  unresolved: Set<string>,
  labels: Record<string, string>,
): boolean => {
  const displayName = resolveCasinoGameLabel(game);
  if (!displayName) return false;

  let changed = false;
  for (const id of getCasinoGameMatchIds(game)) {
    if (!unresolved.has(id)) continue;
    if (displayName === id) continue;
    labels[id] = displayName;
    unresolved.delete(id);
    changed = true;
  }
  return changed;
};

/** Read id → name pairs for the given game ids out of any cached casino-games
 *  infinite-query pages (provider lists / search results the picker already
 *  fetched this session). */
export const collectCasinoGameNamesFromQueryCache = (
  queryClient: QueryClient,
  gameIds: readonly string[],
  existing: Readonly<Record<string, string>> = {},
): Readonly<Record<string, string>> => {
  if (gameIds.length === 0) return existing;

  const normalizedIds = gameIds.map(normalizeCasinoGameId);
  const unresolved = new Set(
    normalizedIds.filter((id) => !existing[id]),
  );
  if (unresolved.size === 0) return existing;

  const next = { ...existing };
  let changed = false;
  const entries = queryClient.getQueriesData<
    InfiniteData<CasinoGamesResponse>
  >({ queryKey: GAME_PASS_CASINO_GAMES_QUERY_KEY });

  for (const [, data] of entries) {
    if (!data?.pages) continue;
    for (const page of data.pages) {
      for (const game of page.games) {
        if (stampLabelForMatchingIds(game, unresolved, next)) {
          changed = true;
        }
      }
    }
    if (unresolved.size === 0) break;
  }

  return changed ? next : existing;
};

/** Merge names from a loaded games page into the label map for currently
 *  selected ids only (avoids retaining labels for unselected catalogue rows). */
export const mergeSelectedCasinoGameLabels = (
  loaded: ReadonlyArray<CasinoGame>,
  selectedIds: ReadonlyArray<string>,
  current: Readonly<Record<string, string>>,
): Readonly<Record<string, string>> => {
  if (loaded.length === 0 || selectedIds.length === 0) return current;

  const selected = new Set(selectedIds.map(normalizeCasinoGameId));
  let changed = false;
  const next = { ...current };

  for (const game of loaded) {
    const displayName = resolveCasinoGameLabel(game);
    for (const id of getCasinoGameMatchIds(game)) {
      if (
        selected.has(id) &&
        displayName !== id &&
        next[id] !== displayName
      ) {
        next[id] = displayName;
        changed = true;
      }
    }
  }

  return changed ? next : current;
};

/** Drop label entries for ids no longer in the selection. */
export const pruneCasinoGameLabels = (
  labels: Readonly<Record<string, string>> | undefined,
  selectedIds: ReadonlyArray<string>,
): Record<string, string> | undefined => {
  if (!labels) return undefined;
  const selected = new Set(selectedIds);
  let changed = false;
  const next: Record<string, string> = {};
  for (const [id, name] of Object.entries(labels)) {
    if (selected.has(id)) {
      next[id] = name;
    } else {
      changed = true;
    }
  }
  if (!changed) return labels as Record<string, string>;
  return Object.keys(next).length > 0 ? next : undefined;
};

/** Provider names to scan first when resolving game ids (e.g. from the task's
 *  `provider` rule), then the rest of the catalogue. */
export const orderCasinoProvidersForLookup = (
  catalogueProviders: ReadonlyArray<string>,
  preferredProviders: ReadonlyArray<string> = [],
): string[] => {
  const preferred = new Set(preferredProviders);
  const prioritized = preferredProviders.filter((name) =>
    catalogueProviders.includes(name),
  );
  const remainder = catalogueProviders.filter((name) => !preferred.has(name));
  return [...prioritized, ...remainder];
};

/** Upper bound on catalogue page requests per resolution walk. Saved tasks can
 *  carry stale ids that no longer exist in the catalogue; without a budget one
 *  such id turns the lookup into a crawl of the entire catalogue (FE-2284). */
export const MAX_CATALOGUE_PAGE_FETCHES = 40;

const normalizeForNameMatch = (text: string): string =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

/** Best-effort display name from a name-search page for an id with no exact
 *  catalogue match: an exact normalized-name hit, else a containment match,
 *  else the top result. A close name beats a raw id on screen (FE-2284), and
 *  exact id matches are always stamped first so this can never override them. */
export const pickClosestCasinoGameName = (
  searchTerm: string,
  games: ReadonlyArray<CasinoGame>,
): string => {
  const target = normalizeForNameMatch(searchTerm);
  if (!target) return "";

  const names = games
    .map((game) => String(game.name ?? "").trim())
    .filter((name) => normalizeForNameMatch(name).length > 0);
  if (names.length === 0) return "";

  const exact = names.find((name) => normalizeForNameMatch(name) === target);
  if (exact) return exact;

  const containment = names.find((name) => {
    const normalized = normalizeForNameMatch(name);
    return normalized.includes(target) || target.includes(normalized);
  });
  return containment ?? names[0];
};

/** Search term for the closest-name fallback, or `undefined` when the id has
 *  no name signal to search with (pure numerics, ids that only differ from
 *  their humanisation by casing). */
const getClosestNameSearchTerm = (id: string): string | undefined => {
  const term = humanizeCasinoGameSlug(id);
  if (!term || term.toLowerCase() === id.toLowerCase()) return undefined;
  return term;
};

/** Walk the casino-games catalogue (provider-by-provider, paginated) until every
 *  requested id has a display name, the fetch budget runs out, or the catalogue
 *  is exhausted. Uses the existing Game Pass admin endpoints — there is no
 *  id-only lookup route. Individual request failures are tolerated so one bad
 *  id or provider page cannot discard names that were already found. */
export const resolveCasinoGameLabelsFromCatalogue = async (
  gameIds: readonly string[],
  existing: Readonly<Record<string, string>> = {},
  options: {
    preferredProviders?: readonly string[];
    fetchProviders?: typeof fetchCasinoProviders;
    fetchGames?: typeof fetchCasinoGames;
    maxPageFetches?: number;
  } = {},
): Promise<Readonly<Record<string, string>>> => {
  const fetchProvidersFn = options.fetchProviders ?? fetchCasinoProviders;
  const fetchGamesFn = options.fetchGames ?? fetchCasinoGames;
  let remainingFetches = options.maxPageFetches ?? MAX_CATALOGUE_PAGE_FETCHES;

  const unresolved = new Set(
    gameIds.map(normalizeCasinoGameId).filter((id) => !existing[id]),
  );
  if (unresolved.size === 0) return existing;

  const labels = { ...existing };

  const absorbGames = (games: ReadonlyArray<CasinoGame>): void => {
    for (const game of games) {
      stampLabelForMatchingIds(game, unresolved, labels);
    }
  };

  const fetchPage = async (
    filter: Parameters<typeof fetchCasinoGames>[0],
    offset: number,
  ): Promise<CasinoGamesResponse | undefined> => {
    if (remainingFetches <= 0) return undefined;
    remainingFetches -= 1;
    try {
      return await fetchGamesFn(filter, offset);
    } catch {
      // One bad request (e.g. a UUID-shaped search term the BE rejects) must
      // not abort the walk for the remaining ids.
      return undefined;
    }
  };

  // Fast path: id-shaped search terms often hit the exact catalogue row first.
  for (const id of Array.from(unresolved)) {
    const page = await fetchPage({ search: id }, 0);
    if (page) absorbGames(page.games);
  }

  // Second pass: search by the humanised slug. Exact id matches in the
  // results are stamped immediately; the closest name is only remembered as
  // a candidate, applied after the exact provider walk comes up empty.
  const closestNameCandidates = new Map<string, string>();
  for (const id of Array.from(unresolved)) {
    const term = getClosestNameSearchTerm(id);
    if (!term) continue;
    const page = await fetchPage({ search: term }, 0);
    if (!page) continue;
    absorbGames(page.games);
    if (!unresolved.has(id)) continue;
    const closest = pickClosestCasinoGameName(term, page.games);
    if (closest && closest !== id) {
      closestNameCandidates.set(id, closest);
    }
  }

  const applyClosestNameCandidates = (): void => {
    closestNameCandidates.forEach((name, id) => {
      if (!unresolved.has(id)) return;
      labels[id] = name;
      unresolved.delete(id);
    });
  };

  if (unresolved.size === 0 || remainingFetches <= 0) {
    applyClosestNameCandidates();
    return labels;
  }

  let providerNames: string[];
  try {
    const { providers } = await fetchProvidersFn();
    providerNames = orderCasinoProvidersForLookup(
      providers.map((provider) => provider.name),
      options.preferredProviders,
    );
  } catch {
    applyClosestNameCandidates();
    return labels;
  }

  for (const provider of providerNames) {
    if (unresolved.size === 0 || remainingFetches <= 0) break;
    let offset = 0;
    while (unresolved.size > 0) {
      const page = await fetchPage({ provider }, offset);
      if (!page || page.games.length === 0) break;
      absorbGames(page.games);
      const nextOffset = getNextCasinoGamesOffset(page);
      // `nextOffset <= offset` guards against a BE that ignores `offset` and
      // keeps returning the first page, which previously looped forever.
      if (nextOffset === undefined || nextOffset <= offset) break;
      offset = nextOffset;
    }
  }

  applyClosestNameCandidates();
  return labels;
};

/** Background-resolve display names for selected game ids that only exist on
 *  the wire (e.g. after loading a saved task from the BE). Runs once per
 *  unresolved id set; prefers providers already selected on the task. */
export const useResolveCasinoGameLabels = (
  gameIds: readonly string[],
  gameLabels: Readonly<Record<string, string>>,
  onChangeGameLabels: ((next: Readonly<Record<string, string>>) => void) | undefined,
  preferredProviders: readonly string[] = [],
): void => {
  const attemptedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!onChangeGameLabels || gameIds.length === 0) return undefined;

    const unresolvedIds = gameIds.filter((id) => !gameLabels[id]);
    if (unresolvedIds.length === 0) return undefined;

    const attemptKey = [
      unresolvedIds.slice().sort().join("\0"),
      preferredProviders.slice().sort().join("\0"),
    ].join("|");
    if (attemptedKeyRef.current === attemptKey) return undefined;

    let cancelled = false;
    attemptedKeyRef.current = attemptKey;

    resolveCasinoGameLabelsFromCatalogue(gameIds, gameLabels, {
      preferredProviders,
    })
      .then((next) => {
        if (cancelled) return;
        const resolvedSomething = gameIds.some(
          (id) => next[id] && next[id] !== gameLabels[id],
        );
        if (resolvedSomething) onChangeGameLabels(next);
      })
      .catch(() => {
        // Leave the humanised-id fallback; allow a remount to retry.
        attemptedKeyRef.current = null;
      });

    return () => {
      cancelled = true;
    };
  }, [gameIds, gameLabels, onChangeGameLabels, preferredProviders]);
};
